import React, { useState } from 'react';

import { SerializedStyles } from '@emotion/react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { InputBase } from '@mui/material';
import Autocomplete, { AutocompleteCloseReason } from '@mui/material/Autocomplete';
import ButtonBase from '@mui/material/ButtonBase';
import Popper, { PopperPlacementType } from '@mui/material/Popper';
import { Theme } from '@mui/material/styles';
import cx from 'classnames';

import styled from '../../styled';
import { colorsPalette } from '../../theme';
import useStyles from './styles';

type SelectProps = {
  className?: string;
  id?: string;
  labelId?: string;
  value?: any | any[];
  onChange?: (any) => void;
  items?: SelectItem[];
  multiple?: boolean;
  anchorRef?: React.RefObject<HTMLElement>;
  widthByAnchor?: boolean;
  search?: boolean;
  popperLabel?: string;
  placement?: PopperPlacementType;
  selectHeaderClass?: string;
  selectBtnClass?: string;
  wrapperCss?: SerializedStyles | (({ theme }: { theme: Theme }) => SerializedStyles);
  selectedItemCss?: SerializedStyles | (({ theme }: { theme: Theme }) => SerializedStyles);
};

type SelectItem = {
  value: string | number;
  display?: string | null;
};

const Input = (params) => {
  const className = params?.className;

  return (
    <InputBase
      className={className}
      ref={params.InputProps.ref}
      inputProps={params.inputProps}
      autoFocus
    />
  );
};

const Select: React.FC<SelectProps> = ({
  className,
  id,
  value,
  onChange,
  items = [],
  multiple = false,
  anchorRef = null,
  widthByAnchor = false,
  search = false,
  popperLabel = '',
  placement = 'bottom-start',
  selectHeaderClass,
  selectBtnClass,
  wrapperCss,
  selectedItemCss,
  ...rest
}) => {
  const anchorEl = anchorRef?.current;
  const width = widthByAnchor && anchorEl?.clientWidth;
  const classes = useStyles(width)();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const handleClick = () => setOpen(true);

  // eslint-disable-next-line
  const handleClose = (event: React.ChangeEvent<{}>, reason: AutocompleteCloseReason) => {
    if (reason === 'toggleInput') {
      return;
    }

    setOpen(false);
  };

  const getOptionSelected = (option, value) => option?.value === value?.value;

  const renderOption = (props, option, { selected }) => {
    return (
      <div {...props} className={cx(classes.optionInner, selected && classes.selected)}>
        <div className={classes.optionText}>{option.display}</div>
        <VisibilityIcon
          style={{ visibility: selected ? 'visible' : 'hidden', color: colorsPalette.gray }}
        />
      </div>
    );
  };

  // eslint-disable-next-line
  const handleInputChange = (event, value, reason) => {
    if (!search) return;

    if (reason === 'reset') {
      setInputValue('');
      return;
    }
    setInputValue(value);
  };

  // eslint-disable-next-line
  const handleOnChange = (event, value, reason) => {
    // @ts-ignore
    onChange(value);
    if (!multiple) {
      setOpen(false);
    }
  };

  return (
    <Wrapper className={className} optionalStyles={wrapperCss}>
      <div>
        <ButtonBase
          data-testid={rest['data-testid'] + 'Title'}
          className={selectBtnClass}
          disableRipple
          aria-describedby={id}
          onClick={handleClick}
        >
          {multiple ? (
            <div className={cx(classes.selectHeader, selectHeaderClass)}>
              <SelectedItem className={cx(classes.selectItems)} optionalStyles={selectedItemCss}>
                {value.map((item, index) => {
                  const isLast = value.length - 1 === index;

                  return (
                    <span key={item?.value} className={classes.selectItem}>
                      {item?.display}
                      {isLast ? '' : ', '}
                    </span>
                  );
                })}
              </SelectedItem>

              <KeyboardArrowDownIcon className={classes.chevronIcon} fontSize="large" />
            </div>
          ) : (
            <div className={cx(classes.selectHeader, selectHeaderClass)}>
              <div className={classes.selectItem}>{value?.display}</div>

              <KeyboardArrowDownIcon className={classes.chevronIcon} fontSize="large" />
            </div>
          )}
        </ButtonBase>
      </div>
      {open && <div className={classes.filtersOverlay} />}
      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        className={classes.popper}
        disablePortal
        modifiers={[
          {
            name: 'flip',
            enabled: false,
          },
          {
            name: 'offset',
            options: {
              offset: [0, -45],
            },
          },
        ]}
      >
        <div>
          <div className={classes.popoverHeader}>
            <div className={classes.selectItem}>{popperLabel}</div>

            <KeyboardArrowDownIcon className={classes.chevronIcon} fontSize="large" />
          </div>
        </div>

        <Autocomplete
          data-testid={rest['data-testid']}
          open={open}
          onClose={handleClose}
          multiple={multiple}
          classes={{
            paper: classes.paper,
            option: classes.option,
            popperDisablePortal: classes.popperDisablePortal,
          }}
          value={value}
          onChange={handleOnChange}
          disableCloseOnSelect
          fullWidth
          disablePortal
          inputValue={inputValue}
          onInputChange={handleInputChange}
          renderTags={() => null}
          noOptionsText="No options"
          isOptionEqualToValue={getOptionSelected}
          renderOption={renderOption}
          options={items}
          getOptionLabel={(option) => option.display || ''}
          renderInput={(params) => (
            <Input className={search ? classes.input : classes.inputHided} {...params} />
          )}
        />
      </Popper>
    </Wrapper>
  );
};

export default Select;

const Wrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})`
  ${({ optionalStyles }) => optionalStyles}
`;
const SelectedItem = styled('div', {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})`
  ${({ optionalStyles }) => optionalStyles}
`;
